import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

const TESTIMONIALS = [
    {
        name: "Rajendra & Priya Mehta",
        location: "Sukher, Udaipur",
        project: "4 BHK Luxury Villa",
        rating: 5,
        text: "Dhanshree built more than a house — they built our family's legacy. The daily site updates, the quality of marble work, the punctual handover — every detail screamed care. Our parents cried at the housewarming. That's the truest review.",
    },
    {
        name: "Vikram Singh Rathore",
        location: "Hiran Magri, Udaipur",
        project: "3 BHK Modern Home",
        rating: 5,
        text: "Transparent quotation, zero hidden costs, and a foreman who treated my plot like his own. From soil testing to the final coat of paint, I got pictures every single evening. I'd recommend Dhanshree to my closest cousins — and I have.",
    },
    {
        name: "Anjali & Sanjay Joshi",
        location: "Bhuwana, Udaipur",
        project: "Renovation + Interior",
        rating: 5,
        text: "We were nervous about renovating our 18-year-old home while living in it. Dhanshree's team worked in phases, kept dust to a minimum, and gave us a home that feels like a 5-star resort. The Vastu alignment was a beautiful touch.",
    },
    {
        name: "Dr. Mahesh Paliwal",
        location: "Ambamata, Udaipur",
        project: "Turnkey 5 BHK Bungalow",
        rating: 5,
        text: "As a doctor with no time for site visits, I needed a builder I could trust blindly. Dhanshree delivered. Premium fittings, modular interiors, designer elevation — all on time and within budget. My wife now calls them 'the dream weavers'.",
    },
];

export default function Testimonials() {
    const [idx, setIdx] = useState(0);
    const t = TESTIMONIALS[idx];

    const go = (d) => setIdx((p) => (p + d + TESTIMONIALS.length) % TESTIMONIALS.length);

    return (
        <section className="relative bg-white py-20 md:py-32 overflow-hidden" data-testid="testimonials-section">
            <div className="absolute top-1/4 -left-40 w-96 h-96 rounded-full bg-[#FF8800] opacity-[0.05] blur-[100px] pointer-events-none" />

            <div className="max-w-5xl mx-auto px-5 md:px-10">
                <div className="text-center mb-12">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="inline-flex items-center gap-3 mb-6"
                    >
                        <span className="w-8 h-px bg-brand-orange" />
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-orange font-medium">Words from our families</span>
                        <span className="w-8 h-px bg-brand-orange" />
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="font-display font-semibold text-brand-ink text-3xl md:text-4xl lg:text-[44px] leading-[1.05] tracking-tight"
                        data-testid="testimonials-heading"
                    >
                        Stories etched in <span className="font-serif-italic font-normal brand-gradient-text">concrete.</span>
                    </motion.h2>
                </div>

                <div className="relative bg-brand-bone rounded-[32px] p-7 md:p-12 border border-brand-line overflow-hidden">
                    <Quote className="absolute top-6 right-6 md:top-10 md:right-10 text-brand-orange/15" size={100} strokeWidth={1} />

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -24 }}
                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                            data-testid={`testimonial-${idx}`}
                        >
                            <div className="flex items-center gap-1 text-brand-orange mb-5">
                                {[...Array(t.rating)].map((_, i) => (
                                    <span key={i} className="text-base">★</span>
                                ))}
                            </div>
                            <blockquote className="font-display font-medium text-xl md:text-[26px] leading-[1.35] tracking-tight text-brand-ink relative z-10">
                                "{t.text}"
                            </blockquote>
                            <div className="mt-9 flex items-center justify-between flex-wrap gap-4">
                                <div>
                                    <div className="font-semibold text-brand-ink">{t.name}</div>
                                    <div className="text-sm text-brand-muted mt-0.5">{t.location} • {t.project}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => go(-1)}
                                        className="w-12 h-12 rounded-full bg-white border border-brand-line text-brand-ink hover:brand-gradient hover:text-white hover:border-transparent transition-all flex items-center justify-center"
                                        data-testid="testimonial-prev"
                                        aria-label="Previous testimonial"
                                    >
                                        <ChevronLeft size={18} />
                                    </button>
                                    <button
                                        onClick={() => go(1)}
                                        className="w-12 h-12 rounded-full bg-white border border-brand-line text-brand-ink hover:brand-gradient hover:text-white hover:border-transparent transition-all flex items-center justify-center"
                                        data-testid="testimonial-next"
                                        aria-label="Next testimonial"
                                    >
                                        <ChevronRight size={18} />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    <div className="mt-8 flex items-center gap-1.5">
                        {TESTIMONIALS.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setIdx(i)}
                                className={`h-1 rounded-full transition-all ${i === idx ? "w-8 brand-gradient" : "w-4 bg-brand-line"}`}
                                aria-label={`Go to testimonial ${i + 1}`}
                                data-testid={`testimonial-dot-${i}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
